<script>
import ApplicantLayout from "./applicant";
import Header from "@/components/header/header.vue";

// Layout for the admin and staff screens. It reuses the applicant shell (soft orange/blue
// background, page container and footer) and only swaps the header for the role-aware one.
export default {
  name: "PortalLayout",
  components: {
    ApplicantLayout,
    Header,
  },
};
</script>

<template>
  <ApplicantLayout>
    <template #header>
      <Header />
    </template>
    <slot />
  </ApplicantLayout>
</template>

<!--
  Theme for every EasyDataTable on the admin / staff screens (they all carry the
  `table-custom-style` class). Kept global because the table renders its own markup.
-->
<style>
.vue3-easy-data-table.table-custom-style {
  --easy-table-border: 0;
  --easy-table-row-border: 1px solid #eef0f6;

  --easy-table-header-font-size: var(--ecu-fs-body);
  --easy-table-header-height: 46px;
  --easy-table-header-font-color: #0b1f4b;
  --easy-table-header-background-color: #f3f4fa;
  --easy-table-header-item-padding: 8px 14px;

  --easy-table-body-even-row-font-color: #1d2a4d;
  --easy-table-body-even-row-background-color: #fff;

  --easy-table-body-row-font-color: #1d2a4d;
  --easy-table-body-row-background-color: #fff;
  --easy-table-body-row-height: 54px;
  --easy-table-body-row-font-size: var(--ecu-fs-body);

  --easy-table-body-row-hover-font-color: #0b1f4b;
  --easy-table-body-row-hover-background-color: #fff8f2;

  --easy-table-body-item-padding: 8px 14px;

  --easy-table-footer-background-color: #fff;
  --easy-table-footer-font-color: #1d2a4d;
  --easy-table-footer-font-size: var(--ecu-fs-body);
  --easy-table-footer-padding: 0px 8px;
  --easy-table-footer-height: 50px;

  --easy-table-rows-per-page-selector-width: 70px;
  --easy-table-rows-per-page-selector-option-padding: 8px;
  --easy-table-rows-per-page-selector-z-index: 1;

  --easy-table-scrollbar-track-color: #f3f4fa;
  --easy-table-scrollbar-color: #f3f4fa;
  --easy-table-scrollbar-thumb-color: #f6c9a4;
  --easy-table-scrollbar-corner-color: #f3f4fa;

  --easy-table-loading-mask-background-color: rgba(255, 255, 255, 0.75);

  background: transparent;
}
.vue3-easy-data-table.table-custom-style .vue3-easy-data-table__main {
  border-radius: 12px;
}
.vue3-easy-data-table.table-custom-style thead th {
  font-weight: 700;
  white-space: nowrap;
}
.vue3-easy-data-table.table-custom-style thead tr th:first-child {
  border-radius: 12px 0 0 12px;
}
.vue3-easy-data-table.table-custom-style thead tr th:last-child {
  border-radius: 0 12px 12px 0;
}
.vue3-easy-data-table.table-custom-style tbody tr:last-child td {
  border-bottom: 0;
}

/*
  Shared building blocks for the admin / staff list pages (toolbar, filters, buttons,
  verify switch ...). Namespaced under .ecu-shell so nothing leaks into other screens.
*/
.ecu-shell .ecu-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px 20px;
  margin-bottom: 16px;
}
.ecu-shell .ecu-toolbar .ecu-card__head {
  margin-bottom: 0;
}
.ecu-shell .ecu-toolbar__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.ecu-shell .ecu-field {
  position: relative;
  display: flex;
  align-items: center;
}
.ecu-shell .ecu-field select,
.ecu-shell .ecu-field input {
  height: 44px;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  background: #fff;
  color: var(--ecu-ink, #1d2a4d);
  font: inherit;
  font-size: 14px;
  box-shadow: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.ecu-shell .ecu-field select {
  appearance: none;
  -webkit-appearance: none;
  min-width: 190px;
  padding: 0 40px 0 16px;
  cursor: pointer;
}
.ecu-shell .ecu-field input {
  width: 270px;
  padding: 0 16px 0 44px;
}
.ecu-shell .ecu-field input::placeholder {
  color: #8b90a3;
}
.ecu-shell .ecu-field select:focus,
.ecu-shell .ecu-field input:focus {
  outline: none;
  border-color: var(--ecu-orange, #f97316);
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.14);
}
.ecu-shell .ecu-field .app-icon {
  position: absolute;
  pointer-events: none;
  color: var(--ecu-navy, #0b1f4b);
}
.ecu-shell .ecu-field--select .app-icon {
  right: 14px;
}
.ecu-shell .ecu-field--search .app-icon {
  left: 15px;
}

/* "Add ..." button next to the filters */
.ecu-shell .ecu-btn-add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  color: #fff;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.25);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.ecu-shell .ecu-btn-add:hover {
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 9.6px 20.8px rgba(242, 97, 15, 0.34);
}

/* table cells */
.ecu-shell .ecu-table-wrap {
  position: relative;
  overflow-x: auto;
}
.ecu-shell .ecu-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  font-weight: 500;
}
.ecu-shell .ecu-date .app-icon {
  color: var(--ecu-muted, #5f6579);
}
.ecu-shell .ecu-strong {
  font-weight: 600;
  color: var(--ecu-navy, #0b1f4b);
}
.ecu-shell .ecu-actions {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
}
.ecu-shell .ecu-btn-view {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 18px;
  border: 1.5px solid #2c3550;
  border-radius: 999px;
  background: #fff;
  color: #1d2a4d;
  font: inherit;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  line-height: 1.3;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease,
    transform 0.18s ease;
}
.ecu-shell .ecu-btn-view:hover {
  background: var(--ecu-orange, #f97316);
  border-color: var(--ecu-orange, #f97316);
  color: #fff;
  transform: translateY(-1px);
}
.ecu-shell .ecu-btn-view:focus-visible,
.ecu-shell .ecu-btn-add:focus-visible {
  outline: 2px solid var(--ecu-orange, #f97316);
  outline-offset: 2px;
}
.ecu-shell .ecu-btn-view--solid {
  background: var(--ecu-orange, #f97316);
  border-color: var(--ecu-orange, #f97316);
  color: #fff;
}
.ecu-shell .ecu-btn-view--solid:hover {
  background: var(--ecu-orange-dark, #e84d00);
  border-color: var(--ecu-orange-dark, #e84d00);
}
.ecu-shell .ecu-btn-view--success {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}
.ecu-shell .ecu-btn-view--success:hover {
  background: #12843c;
  border-color: #12843c;
}
.ecu-shell .ecu-btn-view--danger {
  border-color: #dc2f2f;
  color: #c52222;
}
.ecu-shell .ecu-btn-view--danger:hover {
  background: #dc2f2f;
  border-color: #dc2f2f;
  color: #fff;
}
.ecu-shell .ecu-btn-view.is-disabled,
.ecu-shell .ecu-btn-view:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}
.ecu-shell .ecu-btn-icon {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1.5px solid #cfd3e0;
  border-radius: 50%;
  background: #fff;
  color: #1d2a4d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}
.ecu-shell .ecu-btn-icon:hover,
.ecu-shell .ecu-btn-icon[aria-expanded="true"] {
  background: #fff1e6;
  border-color: var(--ecu-orange, #f97316);
  color: var(--ecu-orange-dark, #e84d00);
}
.ecu-shell .ecu-select-sm {
  appearance: none;
  -webkit-appearance: none;
  min-width: 170px;
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid #e3e6f0;
  border-radius: 10px;
  color: var(--ecu-ink, #1d2a4d);
  font: inherit;
  font-size: var(--ecu-fs-body);
  cursor: pointer;
  background: #fff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230b1f4b' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 10px center / 14px;
}
.ecu-shell .ecu-select-sm:focus {
  outline: none;
  border-color: var(--ecu-orange, #f97316);
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.14);
}

/* verify / active switch */
.ecu-shell .ecu-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
  vertical-align: middle;
}
.ecu-shell .ecu-switch input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}
.ecu-shell .ecu-switch__slider {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background-color: #d4d7e0;
  border-radius: 24px;
  transition: background-color 0.25s ease, box-shadow 0.25s ease;
}
.ecu-shell .ecu-switch__slider::before {
  content: "";
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 2px 5px rgba(11, 31, 75, 0.22);
  transition: transform 0.25s ease;
}
.ecu-shell .ecu-switch input:checked + .ecu-switch__slider {
  background-color: var(--ecu-orange, #f97316);
}
.ecu-shell .ecu-switch input:checked + .ecu-switch__slider::before {
  transform: translateX(20px);
}
.ecu-shell .ecu-switch input:focus-visible + .ecu-switch__slider {
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.25);
}
.ecu-shell .ecu-switch input:disabled,
.ecu-shell .ecu-switch input:disabled + .ecu-switch__slider {
  cursor: not-allowed;
}
.ecu-shell .ecu-switch input:disabled + .ecu-switch__slider {
  opacity: 0.55;
}

/* small text link used in page headings ("Archived Staff members ->") */
.ecu-shell .ecu-text-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: #fff1e6;
  color: var(--ecu-orange-dark, #e84d00);
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  text-decoration: none;
  transition: background 0.18s ease, color 0.18s ease;
}
.ecu-shell .ecu-text-link:hover {
  background: var(--ecu-orange, #f97316);
  color: #fff;
}

@media (max-width: 767px) {
  .ecu-shell .ecu-toolbar__filters,
  .ecu-shell .ecu-field {
    width: 100%;
  }
  .ecu-shell .ecu-field select,
  .ecu-shell .ecu-field input {
    width: 100%;
  }
  .ecu-shell .ecu-btn-add {
    width: 100%;
    justify-content: center;
  }
}

.ecu-shell .ecu-filter-select {
  min-width: 190px;
}
@media (max-width: 767px) {
  .ecu-shell .ecu-filter-select {
    min-width: 0;
  }
}

/*
  Stacked toolbar (admin / staff list cards):
    row 1 -> icon + title
    row 2 -> search (30%) on the left, status filter (15%) and optional primary
             action (25%) on the right.
  Percentages are relative to the card's inner width.
*/
.ecu-shell .ecu-toolbar--stacked {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: flex-start;
  gap: 16px;
}
.ecu-shell .ecu-toolbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}
.ecu-shell .ecu-toolbar--stacked .ecu-field--search {
  flex: 0 0 30%;
  width: 30%;
  min-width: 220px;
}
.ecu-shell .ecu-toolbar--stacked .ecu-field--search input {
  width: 100%;
}
.ecu-shell .ecu-toolbar--stacked .ecu-filter-select {
  flex: 0 0 15%;
  width: 15%;
  min-width: 160px;
  margin-left: auto;
}
.ecu-shell .ecu-toolbar--stacked .ecu-btn-add {
  flex: 0 0 25%;
  width: 25%;
  min-width: 180px;
  justify-content: center;
}
@media (max-width: 991px) {
  .ecu-shell .ecu-toolbar__row {
    flex-wrap: wrap;
  }
  .ecu-shell .ecu-toolbar--stacked .ecu-field--search {
    flex: 1 1 100%;
    width: 100%;
  }
  .ecu-shell .ecu-toolbar--stacked .ecu-filter-select,
  .ecu-shell .ecu-toolbar--stacked .ecu-btn-add {
    flex: 1 1 calc(50% - 6px);
    width: auto;
    margin-left: 0;
  }
}
@media (max-width: 575px) {
  .ecu-shell .ecu-toolbar--stacked .ecu-filter-select,
  .ecu-shell .ecu-toolbar--stacked .ecu-btn-add {
    flex: 1 1 100%;
  }
}

/*
  Detail pages (single staff / single application):
  profile card on the left, data card on the right.
*/
.ecu-shell .ecu-detail {
  display: grid;
  grid-template-columns: 370px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}
.ecu-shell .ecu-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 6px 0 18px;
}
.ecu-shell .ecu-profile__avatar {
  width: 104px;
  height: 104px;
  margin-bottom: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  color: #fff;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 4px #fff, 0 0 0 7px rgba(249, 115, 22, 0.28),
    0 14px 30px rgba(242, 97, 15, 0.22);
}
.ecu-shell .ecu-profile__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ecu-shell .ecu-profile__name {
  margin: 0;
  max-width: 100%;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: var(--ecu-navy, #0b1f4b);
  word-break: break-word;
}
.ecu-shell .ecu-profile__role {
  display: inline-flex;
  padding: 4px 14px;
  border-radius: 999px;
  background: var(--ecu-orange-soft, #fff1e6);
  color: var(--ecu-orange-dark, #e84d00);
  font-size: var(--ecu-fs-small);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.ecu-shell .ecu-profile__role--alt {
  background: #e4efff;
  color: #1d5fd1;
}
.ecu-shell .ecu-stat-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}
.ecu-shell .ecu-stat-pair__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(11, 31, 75, 0.06);
  background: #fff;
  box-shadow: 0 6px 18px rgba(20, 40, 90, 0.06);
}
.ecu-shell .ecu-stat-pair__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ecu-shell .ecu-stat-pair__icon--ok {
  background: #e6f6ec;
  color: #0f8a3c;
}
.ecu-shell .ecu-stat-pair__icon--bad {
  background: #fde9e9;
  color: #c52222;
}
.ecu-shell .ecu-stat-pair__item strong {
  display: block;
  font-size: var(--ecu-fs-h2);
  line-height: 1.1;
  font-weight: 800;
  color: var(--ecu-navy, #0b1f4b);
}
.ecu-shell .ecu-stat-pair__item small {
  font-size: var(--ecu-fs-small);
  color: var(--ecu-muted, #5f6579);
}
.ecu-shell .ecu-info {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ecu-shell .ecu-info__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-top: 1px solid #eef0f6;
}
.ecu-shell .ecu-info__icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--ecu-orange-soft, #fff1e6);
  color: var(--ecu-orange, #f97316);
}
.ecu-shell .ecu-info__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.ecu-shell .ecu-info__label {
  flex-shrink: 0;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-normal);
  color: var(--ecu-muted, #5f6579);
}
.ecu-shell .ecu-info__value {
  min-width: 0;
  text-align: right;
  font-size: var(--ecu-fs-body);
  font-weight: 600;
  color: var(--ecu-navy, #0b1f4b);
  overflow-wrap: anywhere;
}
.ecu-shell .ecu-info__sk {
  display: inline-block;
  width: 150px;
  height: 16px;
}
@media (max-width: 991px) {
  .ecu-shell .ecu-detail {
    grid-template-columns: minmax(0, 1fr);
  }
}

/*
  ---------- modals ----------
  Bootstrap modals are rendered in <body> (outside .ecu-shell), so the skin is global.
  The ecu-m-* classes are the building blocks for modal forms / confirmations.
*/
.modal-backdrop.show {
  background: #0b1f4b;
  opacity: 0.5;
}
.modal-content {
  border: 0;
  border-radius: 20px;
  overflow: visible;
  background: #fff;
  box-shadow: 0 28px 70px rgba(11, 31, 75, 0.3);
  font-family: "Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
  color: #1d2a4d;
}
.modal-content .modal-header {
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eef0f6;
  border-radius: 20px 20px 0 0;
  background: linear-gradient(180deg, #fffaf5 0%, #fff 100%);
}
.modal-content .modal-header .modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: #0b1f4b;
}
.modal-content .modal-header .modal-title::before {
  content: "";
  width: 4px;
  height: 22px;
  border-radius: 4px;
  background: linear-gradient(180deg, #ff8a2a, #f2610f);
}
.modal-content .modal-header .btn-close {
  width: 34px;
  height: 34px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  background-color: #fff1e6;
  background-size: 11px;
  opacity: 1;
  transition: background-color 0.18s ease, transform 0.18s ease;
}
.modal-content .modal-header .btn-close:hover {
  background-color: #ffd9bd;
  transform: rotate(90deg);
}
.modal-content .modal-header .btn-close:focus {
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.2);
}
.modal-content .modal-body {
  padding: 24px;
}

.ecu-m-form {
  margin: 0;
}
.ecu-m-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 16px;
}
.ecu-m-field--wide {
  grid-column: 1 / -1;
}
.ecu-m-label {
  display: block;
  margin: 0 0 7px;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-normal);
  color: #0b1f4b;
}
.ecu-m-input {
  display: block;
  width: 100%;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  background: #fff;
  color: #1d2a4d;
  font: inherit;
  font-size: var(--ecu-fs-body);
  box-shadow: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.ecu-m-input::placeholder {
  color: #8b90a3;
}
.ecu-m-input:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.14);
}
.ecu-m-input:disabled {
  background: #f5f6fa;
  color: #7a8096;
  cursor: not-allowed;
}
/* native file pickers inside modal forms */
.ecu-m-input[type="file"] {
  padding: 0 6px 0 6px;
  line-height: 42px;
  color: #5f6579;
  cursor: pointer;
}
.ecu-m-input[type="file"]::file-selector-button {
  height: 30px;
  margin: 0 12px 0 0;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #fff1e6;
  color: #e84d00;
  font: inherit;
  font-size: var(--ecu-fs-body);
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}
.ecu-m-input[type="file"]::file-selector-button:hover {
  background: #f97316;
  color: #fff;
}
.ecu-m-textarea {
  height: auto;
  min-height: 96px;
  padding: 12px 16px;
  line-height: 1.5;
  resize: vertical;
}
.ecu-m-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.ecu-m-btn--success {
  background: linear-gradient(135deg, #22b45a, #138a3e);
  box-shadow: 0 6.4px 16px rgba(19, 138, 62, 0.25);
}
.ecu-m-btn--danger {
  background: linear-gradient(135deg, #ef4444, #c52222);
  box-shadow: 0 6.4px 16px rgba(197, 34, 34, 0.25);
}
.ecu-m-btn--warning {
  background: linear-gradient(135deg, #f5b301, #d18a00);
  box-shadow: 0 6.4px 16px rgba(209, 138, 0, 0.25);
}
.ecu-m-confirm__icon--ok {
  background: #e6f6ec;
  color: #138a3e;
  box-shadow: 0 0 0 8px #f3fbf6;
}
.ecu-m-confirm__icon--bad {
  background: #fde9e9;
  color: #c52222;
  box-shadow: 0 0 0 8px #fef5f5;
}
.ecu-m-confirm__icon--warn {
  background: #fff6d6;
  color: #a06a00;
  box-shadow: 0 0 0 8px #fffbea;
}
.ecu-m-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}
.ecu-m-actions--center {
  justify-content: center;
}
.ecu-m-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 112px;
  height: 44px;
  padding: 0 24px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  color: #fff;
  font: inherit;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  cursor: pointer;
  box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.25);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}
.ecu-m-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 9.6px 20.8px rgba(242, 97, 15, 0.34);
}
.ecu-m-btn:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 2px;
}
.ecu-m-btn--ghost {
  background: #fff;
  color: #1d2a4d;
  border: 1.5px solid #cfd3e0;
  box-shadow: none;
}
.ecu-m-btn--ghost:hover {
  background: #fff8f2;
  border-color: #f97316;
  color: #e84d00;
  box-shadow: none;
}
.ecu-m-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  padding: 11px 14px;
  border-radius: 12px;
  font-size: var(--ecu-fs-body);
  font-weight: 600;
}
.ecu-m-alert--success {
  background: #e6f6ec;
  color: #0f8a3c;
}
.ecu-m-alert--error {
  background: #fde9e9;
  color: #c52222;
}
.ecu-m-loading {
  display: flex;
  justify-content: center;
  padding: 36px 0;
}
.ecu-m-loading span {
  width: 38px;
  height: 38px;
  border: 4px solid #ffe3cc;
  border-top-color: #f97316;
  border-radius: 50%;
  animation: ecu-m-spin 0.8s linear infinite;
}
@keyframes ecu-m-spin {
  to { transform: rotate(360deg); }
}
.ecu-m-confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 6px 4px 0;
}
.ecu-m-confirm__icon {
  width: 68px;
  height: 68px;
  margin-bottom: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1e6;
  color: #f97316;
  box-shadow: 0 0 0 8px #fff8f2;
}
.ecu-m-confirm h5 {
  margin: 0 0 8px;
  font-size: var(--ecu-fs-h3);
  font-weight: var(--ecu-fw-heading);
  line-height: 1.35;
  color: #0b1f4b;
}
.ecu-m-confirm p {
  margin: 0;
  max-width: 320px;
  font-size: var(--ecu-fs-body);
  color: #5f6579;
}
@media (max-width: 575px) {
  .ecu-m-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .ecu-m-actions .ecu-m-btn {
    flex: 1;
  }
  .modal-content .modal-body {
    padding: 18px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ecu-m-btn, .modal-content .modal-header .btn-close { transition: none; }
  .ecu-m-loading span { animation-duration: 2s; }
}
</style>